import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { Button, Title5 } from '@gafe/common-ui';
import currency from 'currency.js';
import { filter, map } from 'lodash';

import { RequestStatusBar } from '@/components/generic/RequestStatusBar';
import { WarningBanner } from '@/components/generic/WarningBanner';
import { WarningPopOver } from '@/components/generic/WarningPopOver';
import { ContainerWithCurtain } from '@/components/layout/ContainerWithCurtain';
import {
  getStepByName,
  markStepAsCompleted,
  useOrderContext,
  useOrderDispatch,
  useOrderItemsTotalCost,
} from '@/components/modals/SubmitOrder/helpers';
import { OpsNotificationsCard } from '@/components/remote/OpsNotifications/OpsNotificationsCard';
import { SolarOnlyAccess } from '@/components/remote/SolarOnlyAccess/SolarOnyAccess';
import { CalculationMethod } from '@/controllers/Activity';
import { SalesRep } from '@/controllers/SalesRep';
import { useBomTotal } from '@/hooks';
import { AbilityContext } from '@/hooks/ability/Can';
import { useAuth } from '@/hooks/useAuth/useAuth';
import { ROUTES } from '@/router';
import {
  CreateDesignToOrderVm,
  CreateOrderItemVm,
  OrderInventoryType,
  ProposalType,
  WatcherVm,
} from '@/types';

import {
  getOrderTotal,
  isAddonProduct,
  isEquipment,
  isInventory,
  isService,
  isStorageOrderItem,
  OLD_SERVICES_LIST_VIEW,
  productHasZeroQuantity,
  shouldSkipInventoryItem,
} from '../../constants';
import { OrderItemWrapper, StepsName } from '../../types';
import { ProposalDetails } from '../Common/ProposalDetails';
import { isProductAvailable } from '../OrderDetailsStep/components/SelectServices/constants';
import { isDesignService } from '../OrderDetailsStep/components/ServiceDetails/constants';

import { ConfirmationContractDetails } from './components/ConfirmationContractDetails';
import { ConfirmationCostDetails } from './components/ConfirmationCostDetails/ConfirmationCostDetails';
import { ConfirmationOrderDetails } from './components/ConfirmationOrderDetails/ConfirmationOrderDetails';
import { ConfirmationShippingDetails } from './components/ConfirmationShippingDetails';
import { ContractSummary } from './components/ContractSummary';
import { getNonEquipmentItems, nextStepValidation, separateNonEquipmentItems } from './helpers';
import { Props } from './types';

import styles from './ConfirmationStep.module.scss';

export const ConfirmationStep = ({
  calculatedTax,
  calculateTax,
  close,
  createDesignToOrder,
  createdOrderId,
  goToStep,
  isActive,
  opportunity,
  productLineName,
  proposal,
  submitError,
  submitInProgress,
  stepName,
}: Props) => {
  const { userInfo } = useAuth();
  const state = useOrderContext();
  const dispatch = useOrderDispatch();
  const ability = useAbility(AbilityContext);
  const isBulkFulfillmentEnabled = ability.can('view', 'Bulk Fulfillment Program');
  const orderDetailsStep = getStepByName(state, StepsName['Order Details']);
  const shippingStep = getStepByName(state, StepsName['Shipping']);
  const successStep = getStepByName(state, StepsName['Success']);
  const isActivityDistributionModel = state.isDistribution;
  const bomTotal = useBomTotal(state, proposal);
  const [solarOnlyError, setSolarOnlyError] = useState(false);

  const enableSkuStep = stepName === 'Confirmation' || stepName === 'Request Contract';
  const enableSkuFlow: boolean =
    ability.can('view', 'SKU_PRICING') &&
    proposal?.priceCalculationMethod === CalculationMethod.SKU &&
    enableSkuStep &&
    !isBulkFulfillmentEnabled;

  const selectedWatchers = useMemo(
    () => state.order.watcherList.map(watcher => watcher.contactId),
    [state.order.watcherList],
  );

  // if created order id is passed as a prop => navigate to success page
  useEffect(() => {
    if (createdOrderId) {
      markStepAsCompleted(state.wizardHeaderSteps, stepName);
      goToStep?.(successStep);
    }
  }, [createdOrderId, successStep, goToStep]);

  const submitOrder = (): void => {
    const isDesignServicesSelected = state.selectedServices === 'Design Services';
    const isCompleteServicesSelected = state.selectedServices === 'Complete Energy Services';

    const shouldSkipServiceItem = (item: OrderItemWrapper): boolean => {
      if (isService(item)) {
        if (
          !isProductAvailable(item) ||
          !state.selectedServices ||
          (isDesignServicesSelected && !isDesignService(item)) ||
          (isCompleteServicesSelected && isDesignService(item))
        ) {
          return true;
        }
      }

      return false;
    };
    const getAvailableOrderItems = (): OrderItemWrapper[] =>
      state.visibleOrderItems.filter(item => {
        if (
          (!OLD_SERVICES_LIST_VIEW && shouldSkipServiceItem(item)) ||
          (isAddonProduct(item) && !isProductAvailable(item)) ||
          shouldSkipInventoryItem(state.order.inventoryType, item, productLineName) ||
          productHasZeroQuantity(item)
        ) {
          return false;
        }

        return true;
      });

    const mappedOrderItems: CreateOrderItemVm[] = map(getAvailableOrderItems(), oi => oi.orderItem);

    const mappedOrder = {
      ...state.order,
      orderItems: mappedOrderItems,
    };

    const checkIfInventoryTypeIsAvailable = (order: CreateDesignToOrderVm) => {
      const isTimberline = proposal?.proposalType === ProposalType['Timberline Solar'];

      const isInventoryAvailable =
        filter(state.visibleOrderItems, oi => isEquipment(oi) && isInventory(oi)).length > 0;

      if (!isBulkFulfillmentEnabled || !isTimberline) {
        order.inventoryType = null;
      } else if (!isInventoryAvailable) {
        order.inventoryType = OrderInventoryType['Need Inventory'];
      }

      return order;
    };

    // @ts-ignore
    const order = checkIfInventoryTypeIsAvailable(mappedOrder);
    const syncedProposalId = opportunity?.syncedProposalId ?? null;

    // we set the partialReroof as false since we do not need it for distribution model but it is required in SFDC
    if (state.order.partialReRoof === null && Boolean(state.isDistribution)) {
      order.partialReRoof = false;
    }

    if (state.order.solarOnly === null && opportunity?.showSolarOnlyAccess) {
      setSolarOnlyError(true);

      return;
    }

    createDesignToOrder(order, syncedProposalId);
  };

  const goToOrderDetails = useCallback(() => {
    goToStep?.(orderDetailsStep);
  }, [goToStep, orderDetailsStep]);

  const goToShipping = useCallback(() => {
    goToStep?.(shippingStep);
  }, [goToStep, shippingStep]);

  const { total: storageTotal } = useOrderItemsTotalCost(state, isStorageOrderItem);
  const nonEquipmentItems = getNonEquipmentItems(state);
  const proposalAdders = proposal?.adders.map(proposalAdder => proposalAdder.name) ?? [];
  const { servicesTotal, addersTotal } = separateNonEquipmentItems(
    state,
    nonEquipmentItems,
    proposalAdders,
  );

  let orderTotal: number;

  if (isActivityDistributionModel) {
    orderTotal = currency(servicesTotal).add(addersTotal).add(storageTotal).value;
  } else {
    orderTotal = enableSkuFlow ? bomTotal : getOrderTotal(state, productLineName, proposal);
  }

  const finalTotal = orderTotal + (calculatedTax ?? 0);

  const productErrors = nextStepValidation({
    state,
    orderTotal: finalTotal,
    userRole: userInfo?.role,
  });

  const [watchersError, setWatchersError] = useState<boolean>(false);

  const onWatcherError = useCallback(() => {
    setWatchersError(true);
  }, []);

  const onWatcherListChange = useCallback(
    (watchers: SalesRep[]) => {
      setWatchersError(false);

      const watcherList: WatcherVm[] = watchers.map(watcher => ({ contactId: watcher.id }));

      dispatch({
        type: 'UPDATE_ORDER',
        payload: {
          watcherList,
        },
      });
    },
    [dispatch],
  );

  const onToggleSolarOnlyAccess = useCallback(
    (solarOnly: boolean) => {
      dispatch({
        type: 'UPDATE_ORDER',
        payload: {
          solarOnly,
        },
      });
    },
    [dispatch],
  );

  return (
    <div>
      <ContainerWithCurtain
        className={styles.container}
        curtainType="blank"
        showCurtain={submitInProgress}
        transparent
      >
        <ProposalDetails
          orderItems={state.visibleOrderItems}
          purchaseNumber={state.order.purchaseNumber}
          shouldDisplayPurchaseNumber
        />
        <br />
        <ConfirmationContractDetails goToStep={goToOrderDetails} />
        <ConfirmationOrderDetails
          bomTotal={enableSkuFlow ? bomTotal : null}
          calculateTax={calculateTax}
          calculatedTax={calculatedTax}
          goToStep={goToOrderDetails}
          isActive={isActive}
          productLineName={productLineName}
          proposal={proposal}
        />
        <br />
        <ConfirmationCostDetails
          calculateTax={calculateTax}
          calculatedTax={calculatedTax}
          isActive={isActive}
          orderTotal={orderTotal}
          proposal={proposal}
        />
        <br />
        {!state.isPriceHidden && <ContractSummary total={finalTotal} />}
        <ConfirmationShippingDetails goToStep={goToShipping} />
        <br />
        {opportunity?.showSolarOnlyAccess ? (
          <SolarOnlyAccess
            enabled={state.order?.solarOnly}
            hasError={solarOnlyError}
            onToggle={onToggleSolarOnlyAccess}
          />
        ) : null}
        <OpsNotificationsCard
          onSelectedValuesChange={onWatcherListChange}
          onValidationError={onWatcherError}
          renderHeader={<Title5 colorSchema="monochromatic-dark">Notifications</Title5>}
          selectedValues={selectedWatchers}
          validateWatchersOnChange
        />

        <WarningBanner
          alignment="horizontal"
          subTitle={
            !state.isAccessProgram &&
            'Documents & other assets can be added after order is submitted.'
          }
          title={
            state.isAccessProgram
              ? 'Before requesting your contract, please verify all information is correct.'
              : 'Before submitting your order, please verify all information is correct.'
          }
        />
        <br />
        <p className={styles.terms_and_policies}>
          In submitting this order you are agreeing to the GAF Energy{' '}
          <Link className={styles.link} target="_blank" to={ROUTES.Public.TermsAndConditions}>
            Terms of Use
          </Link>{' '}
          policy and{' '}
          <Link className={styles.link} target="_blank" to={ROUTES.Public.TermsOfPurchase}>
            Terms of Purchase
          </Link>{' '}
          policy.
        </p>
      </ContainerWithCurtain>

      <RequestStatusBar
        classes={{
          container: styles.submit_loader,
        }}
        loading={submitInProgress}
        messages={{
          inProgress: 'Submitting the order',
          error: submitError ? 'Unable to submit the order' : '',
        }}
      />

      <div className={styles.stepper_buttons}>
        <Button
          colorSchema="neutral"
          disabled={submitInProgress}
          onClick={close}
          size="large"
          variant="text"
        >
          Cancel
        </Button>
        <WarningPopOver productErrors={productErrors}>
          <Button
            colorSchema="primary"
            disabled={submitInProgress || productErrors.length > 0 || Boolean(watchersError)}
            onClick={submitOrder}
            size="large"
          >
            {state.isAccessProgram ? 'Request Contract' : 'Submit Order'}
          </Button>
        </WarningPopOver>
      </div>
    </div>
  );
};
